import { useCallback, useState, useMemo, useEffect } from 'react';
import { BacklogAiWorkCreationPanelEntryPoint } from '@atlassian/jira-software-backlog-ai-work-creation/src/view/ai-panel/entrypoint.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import type { AiWorkCreatePanelEntryPointSubject } from '../types.tsx';
import type { CardType } from '../../../../../model/types.tsx';

type Props = {
	selectedIssueType: CardType;
	sourceLink?: string;
	onSetAiWorkCreatePanelSubject?: (subject: AiWorkCreatePanelEntryPointSubject) => void;
};

export const useAiWorkCreatePanelEntryPoint = ({
	selectedIssueType,
	sourceLink,
	onSetAiWorkCreatePanelSubject,
}: Props) => {
	// used to notify AI Panel to refetch
	const [retryTrigger, setRetryTrigger] = useState(0);

	const entryPointParams = useMemo(
		() => ({
			selectedIssueType,
			sourceLink: sourceLink ?? '',
			retryTrigger,
		}),
		[sourceLink, retryTrigger, selectedIssueType],
	);

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		BacklogAiWorkCreationPanelEntryPoint,
		entryPointParams,
	);

	useEffect(() => {
		if (sourceLink) {
			entryPointActions.preload();
		}
	}, [entryPointActions, sourceLink]);

	const loadAndShowAiWorkCreatePanel = useCallback(() => {
		onSetAiWorkCreatePanelSubject?.(entryPointReferenceSubject);
		entryPointActions.load();
		setRetryTrigger((prev) => prev + 1);
	}, [entryPointActions, entryPointReferenceSubject, onSetAiWorkCreatePanelSubject]);

	return {
		unloadAiWorkCreatePanel: entryPointActions.unload,
		loadAndShowAiWorkCreatePanel,
	};
};
